import {
  Box,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { pluralize } from '@/utils/format';
import { Ellipsis } from '@/components/Ellipsis';
import { TextInput } from '@/components/Inputs/TextInput';

type NameOption = 'source' | 'destination' | 'custom';

export const MergedGroupNameSelector = ({
  totalAssets,
  nameOption,
  customName,
  sourceGroupName,
  destinationGroupName,
  expanded,
  onNameOptionChange,
  onCustomNameChange,
  onExpandedChange,
}: {
  totalAssets: number;
  nameOption: NameOption;
  customName: string;
  sourceGroupName: string | undefined;
  destinationGroupName: string | undefined;
  expanded: boolean;
  onNameOptionChange: (option: NameOption) => void;
  onCustomNameChange: (name: string) => void;
  onExpandedChange: (expanded: boolean) => void;
}) => {
  const handleAccordionChange = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    onExpandedChange(isExpanded);
    if (isExpanded) {
      if (destinationGroupName) {
        onNameOptionChange('destination');
      } else if (sourceGroupName) {
        onNameOptionChange('source');
      } else {
        onNameOptionChange('custom');
      }
    }
  };

  return (
    <Accordion
      expanded={expanded}
      onChange={handleAccordionChange}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '6px !important',
        backgroundColor: 'action.hover',
        '&:before': { display: 'none' },
        boxShadow: 'none',
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
          Set name for merged group ({pluralize(totalAssets, 'asset', 'assets')} total)
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <RadioGroup value={nameOption} onChange={event => onNameOptionChange(event.target.value as NameOption)}>
          {destinationGroupName && (
            <FormControlLabel
              value='destination'
              control={<Radio size='small' />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Keep destination name:</span>
                  <Ellipsis text={destinationGroupName} sx={{ fontWeight: 600 }} />
                </Box>
              }
            />
          )}
          {sourceGroupName && (
            <FormControlLabel
              value='source'
              control={<Radio size='small' />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <span style={{ whiteSpace: 'nowrap' }}>Keep source name:</span>
                  <Ellipsis text={sourceGroupName} sx={{ fontWeight: 600 }} />
                </Box>
              }
            />
          )}
          <FormControlLabel value='custom' control={<Radio size='small' />} label='Custom name:' />
        </RadioGroup>
        {nameOption === 'custom' && (
          <Box sx={{ mt: 1 }}>
            <TextInput
              placeholder='Enter new group name'
              value={customName}
              onChange={event => onCustomNameChange(event.target.value)}
            />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};
